import { handleSubscribe } from "@api/Billing.js";
import { useMutation } from "@apollo/client";
import DropdownSelect, {
  DropdownOption,
} from "@components/common/dropdown/DropdownSelect";
import {
  faSort,
  faMinusCircle,
  faBan,
  faCheck,
  faTimes,
  faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DEACTIVATE_USER from "@graphql/mutations/DeactivateUser";
import REMOVE_USER_FROM_TEAM from "@graphql/mutations/RemoveUserFromTeam";
import { Tooltip } from "@mui/material";
import { captureException } from "@sentry/react";
import { UNLIMITED_PRICE_ID_MONTHLY } from "@src/constants.js";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";

import { mapRoleIdToRole, nonBillableRoles } from "./utils.js";
import UPDATE_USER_TEAM from "../../../graphql/mutations/UpdateUserTeam.js";
import { useAuth } from "../../../hooks";
import { useTeam } from "../../../hooks/use-team";
import { alert } from "../../common/Alert";
import { ConfirmModal } from "../../common/modals/PopupModals";
import { getUserModificationBillingText } from "../TeamModal/utils.js";

export function RoleDropdown({ user, onChange, error = null }) {
  return (
    <DropdownSelect
      compact
      value={user?.roleId}
      onChange={onChange}
      inputClassNameOverrides={`w-full
                ${
                  error && !user?.roleId
                    ? "border border-red-500"
                    : ""
                }`}
      dataTestId="role-dropdown"
    >
      <Tooltip
        placement="right"
        arrow
        title="Select this role if the user is a veterinarian"
      >
        <div>
          <DropdownOption
            compact
            value={1}
            dataTestId="role-option-DVM"
          >
            {mapRoleIdToRole(1)}
          </DropdownOption>
        </div>
      </Tooltip>
      <Tooltip
        placement="right"
        arrow
        title="Select this role if the user is a practice manager"
      >
        <div>
          <DropdownOption
            compact
            value={2}
            dataTestId="role-option-PracticeManager"
          >
            {mapRoleIdToRole(2)}
          </DropdownOption>
        </div>
      </Tooltip>
      <Tooltip
        placement="right"
        arrow
        title="Select this role if the user is a technician"
      >
        <div>
          <DropdownOption
            compact
            value={4}
            dataTestId="role-option-Technician"
          >
            {mapRoleIdToRole(4)}
          </DropdownOption>
        </div>
      </Tooltip>
      <Tooltip
        placement="right"
        arrow
        title="Select this role if the user is not a doctor, practice manager, or technician"
      >
        <div>
          <DropdownOption
            compact
            value={5}
            dataTestId="role-option-Other"
          >
            {mapRoleIdToRole(5)}
          </DropdownOption>
        </div>
      </Tooltip>
    </DropdownSelect>
  );
}

export function AccountTypeDropdown({
  user,
  onChange,
  disabled = false,
  error = null,
}) {
  return (
    <DropdownSelect
      compact
      value={user.accountType}
      onChange={onChange}
      disabled={disabled}
      inputClassNameOverrides={`w-full
                  ${
                    error && !user.accountType
                      ? "border border-red-500"
                      : ""
                  }`}
    >
      <DropdownOption compact value={"basic"}>
        Free Plan
      </DropdownOption>
      <DropdownOption compact value={"unlimited"}>
        Pro Plan
      </DropdownOption>
    </DropdownSelect>
  );
}

export function EditExistingTeamMembers({
  team,
  isCurrentUserAnAdmin,
  refetchTeam,
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  //constants
  const teamUuid = team?.uuid;
  const { allowUserDeactivation } = useFlags();
  const [editingUser, setEditingUser] = useState({});

  //state
  const [billingIncrementText, setBillingIncrementText] =
    useState("");
  const [isShowingUpgradeModal, setIsShowingUpgradeModal] =
    useState(false);
  const [isSortReverse, setIsSortReverse] = useState(false);
  const [isShowingRemoveModal, setIsShowingRemoveModal] =
    useState(false);
  const [isShowingDeactivateModal, setIsShowingDeactivateModal] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sortedUsers, setSortedUsers] = useState([]);
  const savingUser = useRef(false);

  //hooks
  const { userUuid, isOrgAdmin } = useAuth();
  const { refreshCurrentTeam } = useTeam();

  //mutations
  const [removeUserFromTeam] = useMutation(REMOVE_USER_FROM_TEAM);
  const [updateUserTeam] = useMutation(UPDATE_USER_TEAM);
  const [deactivateUser] = useMutation(DEACTIVATE_USER);

  //effects
  useEffect(() => {
    if (team) {
      setSortedUsers(team?.users);
    }
  }, [team]);

  //functions
  const sortUsers = useCallback(
    (sortBy) => {
      setIsSortReverse(!isSortReverse);

      const sorted = [...sortedUsers]?.sort((a, b) => {
        const aValue = a[sortBy] ?? "";
        const bValue = b[sortBy] ?? "";

        if (
          typeof aValue === "boolean" &&
          typeof bValue === "boolean"
        ) {
          // Convert booleans to numbers (true = 1, false = 0) and compare
          return isSortReverse
            ? (bValue ? 1 : 0) - (aValue ? 1 : 0)
            : (aValue ? 1 : 0) - (bValue ? 1 : 0);
        } else if (
          typeof aValue === "number" ||
          typeof bValue === "number"
        ) {
          // Handle numeric sorting
          return isSortReverse ? bValue - aValue : aValue - bValue;
        } else {
          // Convert values to strings and handle string sorting with localeCompare
          return isSortReverse
            ? String(bValue).localeCompare(String(aValue))
            : String(aValue).localeCompare(String(bValue));
        }
      });

      setSortedUsers(sorted);
    },
    [isSortReverse, sortedUsers],
  );

  useEffect(() => {
    if (!editingUser?.uuid) {
      return;
    }
    setBillingIncrementText(
      getUserModificationBillingText(editingUser),
    );
  }, [editingUser]);

  const handleRemoveClick = (user) => {
    setSelectedUser(user);
    setIsShowingRemoveModal(true);
  };

  const handleSaveUser = async (user) => {
    if (savingUser.current) {
      return;
    }
    savingUser.current = true;
    try {
      const { data } = await updateUserTeam({
        variables: {
          teamUuid: teamUuid,
          userUuid: user.uuid,
          ...editingUser,
        },
      });
      refreshCurrentTeam(team);
      refetchTeam();
      const billingImplication =
        data?.updateUserTeam?.billingImplication;

      alert(
        "success",
        `${user?.firstName} ${user?.lastName} has been modified. ${
          billingImplication === 0
            ? ""
            : "Your billing has been updated accordingly."
        }`,
      );
      mixpanel.track("Edit user", {
        teamUuid: teamUuid,
        userUuid: user.uuid,
      });
      cancelEditingUser();
    } catch (error) {
      if (
        error.message ===
        "Please upgrade your plan to modify your team members' subscriptions"
      ) {
        setIsShowingUpgradeModal(true);
      } else {
        alert("error", error.message);
      }
      mixpanel.track("Edit user error", {
        teamUuid: teamUuid,
        userUuid: user.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
    }
    savingUser.current = false;
  };

  const handleEditClick = (user) => {
    setEditingUser({
      uuid: user.uuid,
      roleId: user.roleId,
      accountType: user.accountType,
      isAdmin: user.isAdmin,
      originalAccountType: user.accountType,
      originalRoleId: user.roleId,
    });
  };

  const cancelEditingUser = () => {
    setBillingIncrementText("");
    setEditingUser({});
  };

  const handleDeactivateClick = (user) => {
    setSelectedUser(user);
    setIsShowingDeactivateModal(true);
    refreshCurrentTeam(team);
  };

  const handleConfirmRemoveFromTeam = async () => {
    try {
      await removeUserFromTeam({
        variables: {
          teamUuid: teamUuid,
          userUuid: selectedUser?.uuid,
        },
      });
      setIsShowingRemoveModal(false);
      setSortedUsers((prevUsers) =>
        prevUsers.filter((user) => user.uuid !== selectedUser.uuid),
      );
      refreshCurrentTeam(team);
      refetchTeam();

      alert(
        "success",
        `${selectedUser?.firstName} ${selectedUser?.lastName} has been removed from the team.`,
      );
      mixpanel.track("Remove user from team", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
      });
    } catch (error) {
      alert(
        "error",
        `Error removing user from team: ${error.message}`,
      );
      mixpanel.track("Remove user from team error", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
    }
  };

  const handleConfirmDeactivateUser = async () => {
    const rmTeamUserInput = {
      teamUuid: team?.uuid,
      userUuid: selectedUser?.uuid,
      orgUuid: team?.orgUuid || team?.organization?.uuid,
    };
    try {
      await deactivateUser({
        variables: { rmTeamUserInput },
      });
      setIsShowingDeactivateModal(false);
      setSortedUsers((prevUsers) =>
        prevUsers.filter((user) => user.uuid !== selectedUser.uuid),
      );
      refreshCurrentTeam(team);
      refetchTeam();
      alert(
        "success",
        `${selectedUser?.firstName} ${selectedUser?.lastName} has been deactivated.`,
      );
      mixpanel.track("Deactivate user", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
      });
    } catch (error) {
      alert("error", `Error deactivating user: ${error.message}`);
      mixpanel.track("Deactivate user error", {
        teamUuid: teamUuid,
        userUuid: selectedUser?.uuid,
        orgUuid: team?.orgUuid || team?.organization?.uuid,
        error: error,
      });
      captureException(error);
    }
  };

  //render
  return (
    <div className="bg-gray-100/70 dark:bg-gray-700/30 border border-gray-50 dark:border-gray-700/50 rounded-lg py-2 px-2 w-full my-4">
      <table className="table-fixed w-full dark:text-gray-300 text-left">
        <thead>
          <tr className="border-b dark:border-gray-700/50">
            <TableHeader
              title="First Name"
              sortBy="firstName"
              sortUsers={sortUsers}
            />
            <TableHeader
              title="Last Name"
              sortBy="lastName"
              sortUsers={sortUsers}
            />
            <TableHeader
              title="Email"
              sortBy="email"
              sortUsers={sortUsers}
            />
            <TableHeader
              title="SOAPs Created"
              sortBy="numAppointmentsUsed"
              sortUsers={sortUsers}
              isCentered={true}
            />
            <TableHeader
              title="Role"
              sortBy="roleId"
              sortUsers={sortUsers}
            />
            <TableHeader
              title="Team Admin"
              sortBy="isAdmin"
              sortUsers={sortUsers}
              isCentered={true}
            />
            {team?.hasBillingSetup && (
              <TableHeader
                title="Account Plan"
                sortBy="accountType"
                sortUsers={sortUsers}
              />
            )}
            {isCurrentUserAnAdmin && (
              <>
                <th className="font-semibold text-sm text-center">
                  Remove
                </th>
                {allowUserDeactivation && (
                  <th className="font-semibold text-sm text-center">
                    Deactivate
                  </th>
                )}
                <th className="font-semibold text-sm"></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedUsers?.map((user, index) => {
            const isCurrentUser = user.uuid === userUuid;
            const isEditingUser = user.uuid === editingUser?.uuid;
            return (
              <tr
                key={index}
                data-testid={`team-member-row-${user.uuid}`}
                data-testid-name={`team-member-${user.firstName}-${user.lastName}`}
                className="even:bg-transparent dark:odd:bg-gray-800 odd:bg-white dark:even:bg-transparent"
              >
                <td>
                  <h5 className="text-sm pl-2 pr-2 truncate overflow-hidden text-ellipsis">
                    {user?.firstName}
                  </h5>
                </td>
                <td>
                  <h5 className="text-sm pr-2 truncate overflow-hidden text-ellipsis">
                    {user?.lastName}
                  </h5>
                </td>
                <td className="py-2">
                  <h5 className="text-sm pr-2 truncate overflow-hidden text-ellipsis">
                    {user?.email}
                  </h5>
                </td>
                <td className="text-center pr-4">
                  <h5 className="text-sm">
                    {user?.numAppointmentsUsed}
                  </h5>
                </td>

                <td>
                  {isEditingUser ? (
                    <RoleDropdown
                      user={editingUser}
                      onChange={(_, value) => {
                        setEditingUser((prev) => ({
                          ...prev,
                          roleId: value,
                        }));
                      }}
                    />
                  ) : (
                    <h5
                      className="text-sm"
                      data-testid={`user-role-${user.uuid}`}
                      data-testid-name={`user-role-${user.firstName}-${user.lastName}`}
                    >
                      {mapRoleIdToRole(user?.roleId)}
                    </h5>
                  )}
                </td>
                <td className="text-center pr-4">
                  {isEditingUser ? (
                    <div className="flex justify-center items-center">
                      <div className="relative flex items-center justify-center">
                        <input
                          checked={editingUser.isAdmin || false}
                          onChange={(e) => {
                            setEditingUser((prev) => ({
                              ...prev,
                              isAdmin: e.target.checked,
                            }));
                          }}
                          type="checkbox"
                          data-testid="admin-checkbox"
                          className="appearance-none block mx-auto rounded-md bg-white dark:bg-gray-700 border-2 border-indigo-500 checked:bg-indigo-500 dark:checked:bg-indigo-500 checked:border-transparent h-6 w-6 cursor-pointer transition-all"
                        />
                        {editingUser?.isAdmin && (
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-white absolute inset-[6px] flex items-center justify-center pointer-events-none"
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <h5 className="text-sm flex justify-center items-center">
                      {user?.isAdmin ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="text-green-500"
                          data-testid={`is-admin-icon-${user.uuid}`}
                          data-testid-name={`is-admin-icon-${user.firstName}-${user.lastName}`}
                        />
                      ) : (
                        ""
                      )}
                    </h5>
                  )}
                </td>

                {team?.hasBillingSetup && (
                  <td className="pr-2">
                    {isEditingUser ? (
                      <AccountTypeDropdown
                        user={editingUser}
                        onChange={(_, value) => {
                          setEditingUser((prev) => ({
                            ...prev,
                            accountType: value,
                          }));
                        }}
                      />
                    ) : (
                      <h5 className="text-sm">
                        {!user?.isSelfSubscribed &&
                          !nonBillableRoles.includes(user?.roleId) &&
                          user?.accountType === "unlimited" && (
                            <Tooltip
                              arrow
                              title="This user's subscription is being paid for"
                            >
                              <FontAwesomeIcon
                                icon={faMoneyBill}
                                className="text-green-500 mr-1"
                              ></FontAwesomeIcon>
                            </Tooltip>
                          )}
                        {!user?.isSelfSubscribed &&
                          nonBillableRoles.includes(user?.roleId) &&
                          user?.accountType === "unlimited" && (
                            <Tooltip
                              arrow
                              title="This user's subscription is free"
                            >
                              <FontAwesomeIcon
                                icon={faMoneyBill}
                                className=" mr-1"
                              ></FontAwesomeIcon>
                            </Tooltip>
                          )}
                        {user?.isSelfSubscribed &&
                          user?.accountType === "unlimited" && (
                            <Tooltip
                              arrow
                              title="This user is paying for their own subscription"
                            >
                              <FontAwesomeIcon
                                icon={faMoneyBill}
                                className="text-red-500 mr-1"
                              ></FontAwesomeIcon>
                            </Tooltip>
                          )}
                        {user?.accountType === "basic"
                          ? "Free Plan"
                          : user?.accountType === "unlimited"
                          ? "Pro Plan"
                          : "Free Plan"}
                      </h5>
                    )}
                  </td>
                )}
                {isCurrentUserAnAdmin && (
                  <>
                    <td className="text-center">
                      <FontAwesomeIcon
                        icon={faMinusCircle}
                        className={`${
                          isCurrentUser ||
                          (user?.isAdmin && !isOrgAdmin)
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-red-500 cursor-pointer hover:text-red-700"
                        }`}
                        onClick={() => {
                          if (
                            isCurrentUser ||
                            (user?.isAdmin && !isOrgAdmin)
                          ) {
                            return;
                          }
                          handleRemoveClick(user);
                        }}
                        data-testid={`remove-user-${user.uuid}`}
                        data-testid-name={`remove-user-${user.firstName}-${user.lastName}`}
                      />
                    </td>
                    {allowUserDeactivation && (
                      <td className="text-center">
                        {user?.isDisabled ? (
                          <Tooltip
                            arrow
                            title="User is deactivated. Please contact support to re-enable the user"
                          >
                            <span>
                              <FontAwesomeIcon
                                icon={faBan}
                                className="text-decoration-line-through text-gray-500 cursor-not-allowed pointer-events-none"
                              />
                            </span>
                          </Tooltip>
                        ) : (
                          <FontAwesomeIcon
                            icon={faBan}
                            className={`${
                              user?.isDisabled
                                ? "text-yellow-400 cursor-not-allowed pointer-events-none"
                                : isCurrentUser ||
                                  (user?.isAdmin && !isOrgAdmin)
                                ? "text-gray-400 cursor-not-allowed"
                                : "text-red-500 cursor-pointer hover:text-red-700"
                            }`}
                            onClick={() => {
                              handleDeactivateClick(user);
                            }}
                            data-testid={`deactivate-user-${user.uuid}`}
                            data-testid-name={`deactivate-user-${user.firstName}-${user.lastName}`}
                          />
                        )}
                      </td>
                    )}
                    <td>
                      {user?.isAdmin && !isOrgAdmin ? (
                        ""
                      ) : (
                        <div className="flex items-center gap-3 px-2">
                          {isEditingUser ? (
                            <div className="mx-auto flex gap-x-2">
                              <button
                                type="reset"
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700 transition-all focus:outline-none"
                                onClick={() => cancelEditingUser()}
                              >
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  size="md"
                                />
                              </button>
                              <button
                                type="submit"
                                className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-green-600 hover:bg-indigo-200 hover:text-green-700 transition-all focus:outline-none"
                                onClick={() => handleSaveUser(user)}
                                data-testid="save-user-button"
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  size="md"
                                />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="submit"
                              className={`w-full font-medium text-white bg-indigo-500 hover:bg-indigo-600 rounded-full text-sm text-center cursor-pointer py-1 px-3 transition-all focus:outline-none`}
                              disabled={editingUser?.uuid}
                              onClick={() => handleEditClick(user)}
                              data-testid={`edit-user-${user.uuid}`}
                              data-testid-name={`edit-user-${user.firstName}-${user.lastName}`}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {billingIncrementText && (
        <div className="m-4 p-4 border rounded-md border-yellow-500 bg-yellow-500/10 dark:bg-yellow-500/50">
          {billingIncrementText}
        </div>
      )}
      {isShowingUpgradeModal && (
        <ConfirmModal
          shouldShow={isShowingUpgradeModal}
          hideModal={() => setIsShowingUpgradeModal(false)}
          title={"Upgrade Your Account"}
          cancelText={"I'll Upgrade Later"}
          confirmText={"Upgrade"}
          cancelAction={() => setIsShowingUpgradeModal(false)}
          confirmAction={() => {
            mixpanel.track("Upgrade Plan Clicked", {
              featureName: "Team Management",
            });

            handleSubscribe({
              priceId: UNLIMITED_PRICE_ID_MONTHLY,
              route: currentPath,
            });
          }}
        >
          In order to upgrade team members to the Unlimited plan, you
          must first upgrade your own account. If you do not want to
          be the primary billing contact, please set up another admin
          on the team who will become the primary billing contact.
        </ConfirmModal>
      )}
      {isShowingRemoveModal && (
        <ConfirmModal
          shouldShow={isShowingRemoveModal}
          hideModal={() => setIsShowingRemoveModal(false)}
          title="Remove from Team"
          confirmText="Remove"
          confirmAction={handleConfirmRemoveFromTeam}
          cancelText="Cancel"
          cancelAction={() => setIsShowingRemoveModal(false)}
        >
          <div>
            Are you sure you want to remove{" "}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>{" "}
            from{" "}
            <strong>{team?.name ? team?.name : "the team"}</strong>?
          </div>
          <div className="bg-red-100 dark:bg-red-600/50 border-red-500 border p-2 rounded-md my-4">
            This means you will no longer be able to view their notes
            or templates. If you are paying for their subscription
            they will be downgraded to a free account and your billing
            will be adjusted.
          </div>
        </ConfirmModal>
      )}
      {isShowingDeactivateModal && (
        <ConfirmModal
          shouldShow={isShowingDeactivateModal}
          hideModal={() => setIsShowingDeactivateModal(false)}
          title={`Deactivate ${selectedUser?.firstName} ${selectedUser?.lastName}`}
          confirmText="Deactivate"
          confirmAction={handleConfirmDeactivateUser}
          cancelText="Cancel"
          cancelAction={() => setIsShowingDeactivateModal(false)}
        >
          <p>
            Are you sure you want to deactivate the account of{" "}
            <strong>
              {selectedUser?.firstName} {selectedUser?.lastName}
            </strong>
            ? <br />
            <div className="bg-red-100 dark:bg-red-600/50 border-red-500 border p-2 rounded-md mt-4">
              This will lock them out of their Scribenote account and
              you will need to contact our support team to reactivate
              it.
            </div>
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}

function TableHeader({
  sortBy,
  title,
  sortUsers = () => {},
  isCentered = false,
}) {
  return (
    <th
      className={`font-semibold text-sm cursor-pointer ${
        isCentered ? "text-center pr-4" : "text-left"
      }`}
      onClick={sortBy ? () => sortUsers(sortBy) : null}
    >
      {title}
      {sortBy && (
        <FontAwesomeIcon icon={faSort} size="xs" className="ml-1" />
      )}
    </th>
  );
}
